import { AttendanceCell, CellStatus } from "@/lib/types";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatTimeIST } from "@/lib/timezone";
import { MessageSquareWarning } from "lucide-react";

function cellLabel(cell: AttendanceCell): string {
  if (cell.status === "L" && cell.late_minutes != null) {
    return `L${cell.late_minutes}`;
  }
  if (cell.status === "pending") return "—";
  return cell.status;
}

const statusStyles: Record<CellStatus, string> = {
  P: "bg-emerald-100 text-emerald-800 border-emerald-200",
  L: "bg-amber-100 text-amber-900 border-amber-200",
  A: "bg-red-100 text-red-800 border-red-200",
  O: "bg-sky-100 text-sky-800 border-sky-200",
  S: "bg-rose-100 text-rose-800 border-rose-200",
  TP: "bg-teal-100 text-teal-800 border-teal-200",
  H: "bg-violet-100 text-violet-800 border-violet-200",
  pending: "bg-slate-100 text-slate-500 border-slate-200",
};

interface AttendanceCellBadgeProps {
  cell: AttendanceCell | null;
  showNotice?: boolean;
}

export function AttendanceCellBadge({
  cell,
  showNotice = false,
}: AttendanceCellBadgeProps) {
  if (!cell) {
    return <span className="text-slate-300">·</span>;
  }

  const content = (
    <span className="inline-flex items-center gap-1">
      <span
        className={cn(
          "inline-flex min-w-[2rem] items-center justify-center rounded border px-1.5 py-0.5 text-xs font-semibold tabular-nums",
          statusStyles[cell.status]
        )}
      >
        {cellLabel(cell)}
      </span>
      {showNotice && cell.has_notice && (
        <MessageSquareWarning
          className="h-3.5 w-3.5 text-saffron-600"
          aria-label="Notice submitted"
        />
      )}
    </span>
  );

  const hasTooltip =
    cell.first_punch_at ||
    cell.verify_mode ||
    cell.holiday_name ||
    cell.is_overridden ||
    (showNotice && cell.notice_reason);

  if (!hasTooltip) return content;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="inline-flex">
            {content}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">
          {cell.first_punch_at && (
            <p>First punch: {formatTimeIST(cell.first_punch_at)}</p>
          )}
          {cell.verify_mode && (
            <p className="capitalize">Verify: {cell.verify_mode}</p>
          )}
          {cell.holiday_name && <p>Holiday: {cell.holiday_name}</p>}
          {cell.is_overridden && cell.override_reason && (
            <p>Override: {cell.override_reason}</p>
          )}
          {showNotice && cell.notice_reason && (
            <p>Notice: {cell.notice_reason}</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function statusColorClass(status: CellStatus): string {
  return statusStyles[status] ?? statusStyles.pending;
}
